import { useTranslation } from 'next-i18next'
import { type FC } from 'react'
import { useTheme } from 'styled-components'

import { LoadingBlockContainer } from '@/components/srp/srp-loading/LoadingBlockContainer'
import { SRP_LISTING_LIMIT } from '@/constants/pageSettings'
import { getCurrentPageFromPagination } from '@/domain/srp/getCurrentPageFromPaginationData'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'

import { SrpResultsText } from './styled'

type SrpResultsProps = {
  loading?: boolean
}

export const SrpResults: FC<SrpResultsProps> = ({ loading = false }) => {
  const { data } = useGetSearchResultsData()

  const { t } = useTranslation('srp')
  const { colors } = useTheme()

  const {
    totalCount: totalListings = 0,
    offset = 0,
    limit = SRP_LISTING_LIMIT,
  } = data?.pagination || {}
  const currentPage = getCurrentPageFromPagination({ offset, limit })

  const getResultsCounts = () => {
    const intl = 'header.results.count'
    const counts = { intl, params: {} }

    /**
     * If there are no listings
     */
    if (totalListings === 0) {
      counts.params = { start: 0, end: 0, total: 0 }
      return counts
    }

    /**
     * If the total of listings is less than the max shown in a page
     */
    if (totalListings < SRP_LISTING_LIMIT) {
      counts.params = { start: 1, end: totalListings, total: totalListings }
      return counts
    }

    /**
     * Calculate based on the current page
     * If the page can show more items than the total
     */
    const pageCountMax = currentPage * SRP_LISTING_LIMIT
    const moreThanTotal = pageCountMax > totalListings

    counts.params = {
      start: (currentPage - 1) * SRP_LISTING_LIMIT + 1,
      end: moreThanTotal ? totalListings : pageCountMax,
      total: totalListings,
    }

    return counts
  }

  const resultsCounts = getResultsCounts()

  return (
    <>
      {loading ? (
        <LoadingBlockContainer variant="inline" bottom="0rem" height="1.75rem" width="25%" />
      ) : (
        <SrpResultsText
          aria-live="polite"
          color={colors.grey.primary}
          data-testid="srp-results"
          size="small"
        >
          {t(resultsCounts.intl, { ...resultsCounts.params })}
        </SrpResultsText>
      )}
    </>
  )
}
